/**
 * Export Utilities
 * Handles Excel and PDF exports for SSP calculations
 */

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { formatCurrency, formatPercent } from './sspCalculations';
import {
  SSP_WEEKLY_RATE,
  SSP_WEEKLY_RATE_2025,
  SSP_WEEKLY_RATE_2026,
  LOWER_EARNINGS_LIMIT,
  WAITING_DAYS_CURRENT,
  WAITING_DAYS_2026,
} from '../data/constants';

/**
 * Export workforce model to Excel
 * @param {object} data - Model data to export
 * @param {string} scenarioName - Name of the scenario
 */
export function exportToExcel(data, scenarioName = 'SSP Model') {
  const { cohortResults, totals, sicknessPattern, options } = data;
  
  const workbook = XLSX.utils.book_new();
  
  // Summary sheet
  const summaryData = [
    ['UK SSP Cost Model', '', '', ''],
    ['Scenario:', scenarioName, '', ''],
    ['Generated:', new Date().toLocaleString('en-GB'), '', ''],
    ['', '', '', ''],
    ['SUMMARY', '', 'Current Rules', 'April 2026'],
    ['Total Employees', '', totals.employeeCount, totals.employeeCount],
    ['Employees Below LEL', '', totals.employeesBelowLEL, '0 (N/A)'],
    ['Total Sick Days (Annual)', '', totals.totalSickDays.toFixed(0), totals.totalSickDays.toFixed(0)],
    ['Rostered Sick Days', '', totals.rosteredSickDays.toFixed(0), totals.rosteredSickDays.toFixed(0)],
    ['SSP Days Paid', '', totals.currentSSPDays.toFixed(0), totals.newSSPDays.toFixed(0)],
    ['SSP Cost', '', formatCurrency(totals.currentSSPCost), formatCurrency(totals.newSSPCost)],
    ['Backfill Cost', '', formatCurrency(totals.currentBackfillCost), formatCurrency(totals.newBackfillCost)],
    ['Total Cost', '', formatCurrency(totals.currentTotalCost), formatCurrency(totals.newTotalCost)],
    ['', '', '', ''],
    ['IMPACT ANALYSIS', '', '', ''],
    ['Additional SSP Cost', '', '', formatCurrency(totals.sspCostIncrease)],
    ['SSP Cost Increase %', '', '', formatPercent(totals.percentSSPIncrease)],
    ['Additional Days Paid', '', '', totals.additionalDaysPaid.toFixed(0)],
    ['Cost Per Employee (Current)', '', formatCurrency(totals.currentCostPerEmployee), ''],
    ['Cost Per Employee (2026)', '', '', formatCurrency(totals.newCostPerEmployee)],
  ];
  
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
  
  // Cohort details sheet
  const cohortHeaders = [
    'Cohort', 'Employees', 'Weekly Earnings', 'Meets LEL', 
    'Total Episodes', 'Sick Days', 'Rostered Days',
    'Current SSP Days', 'Current SSP Cost', 'Current Backfill', 'Current Total',
    '2026 SSP Days', '2026 SSP Cost', '2026 Backfill', '2026 Total',
    'SSP Increase', '% Increase'
  ];
  
  const cohortRows = cohortResults.map(c => [
    c.cohort,
    c.employeeCount,
    formatCurrency(c.weeklyEarnings),
    c.meetsLEL ? 'Yes' : 'No',
    c.totalEpisodes.toFixed(1),
    c.totalSickDays.toFixed(0),
    c.rosteredSickDays.toFixed(0),
    c.current.daysPaid.toFixed(0),
    formatCurrency(c.current.sspCost),
    formatCurrency(c.current.backfillCost),
    formatCurrency(c.current.totalCost),
    c.new2026.daysPaid.toFixed(0),
    formatCurrency(c.new2026.sspCost),
    formatCurrency(c.new2026.backfillCost),
    formatCurrency(c.new2026.totalCost),
    formatCurrency(c.impact.additionalSSPCost),
    isFinite(c.impact.percentIncrease) ? formatPercent(c.impact.percentIncrease) : 'N/A',
  ]);
  
  const cohortData = [cohortHeaders, ...cohortRows];
  const cohortSheet = XLSX.utils.aoa_to_sheet(cohortData);
  XLSX.utils.book_append_sheet(workbook, cohortSheet, 'Cohort Details');
  
  // Sickness pattern sheet
  const patternData = [
    ['Sickness Pattern Configuration', ''],
    ['', ''],
    ['Avg Episodes per Employee per Year', sicknessPattern.avgEpisodesPerEmployee],
    ['', ''],
    ['Absence Duration Distribution', '%'],
    ['1 Day', sicknessPattern.distribution.oneDay],
    ['2 Days', sicknessPattern.distribution.twoDay],
    ['3 Days', sicknessPattern.distribution.threeDay],
    ['4-7 Days', sicknessPattern.distribution.fourToSeven],
    ['8+ Days', sicknessPattern.distribution.eightPlus],
    ['', ''],
    ['Average Lengths', 'Days'],
    ['4-7 Day Absences (avg)', sicknessPattern.avgLengths.fourToSeven],
    ['8+ Day Absences (avg)', sicknessPattern.avgLengths.eightPlus],
  ];
  
  const patternSheet = XLSX.utils.aoa_to_sheet(patternData);
  XLSX.utils.book_append_sheet(workbook, patternSheet, 'Sickness Pattern');
  
  // Assumptions sheet
  const assumptionsData = [
    ['Key Assumptions & Notes', ''],
    ['', ''],
    ['SSP Weekly Rate (2025/26)', formatCurrency(SSP_WEEKLY_RATE_2025)],
    ['SSP Weekly Rate (2026/27)', formatCurrency(SSP_WEEKLY_RATE_2026)],
    ['Lower Earnings Limit (Current)', `${formatCurrency(LOWER_EARNINGS_LIMIT)}/week`],
    ['Lower Earnings Limit (2026)', 'Abolished'],
    ['Waiting Days (Current)', `${WAITING_DAYS_CURRENT} days`],
    ['Waiting Days (2026)', `${WAITING_DAYS_2026} days (Day 1 payment)`],
    ['Lower Earner Rate (2026)', '80% of AWE or standard rate, whichever is lower'],
    ['', ''],
    ['Notes:', ''],
    ['- Current rules: SSP only payable for absences of 4+ days, from day 4', ''],
    ['- 2026 rules: SSP payable from day 1 for any absence', ''],
    ['- 2026 rules: No LEL threshold - all employees eligible', ''],
    ['- Backfill costs assume shifts need to be covered', ''],
    ['- Rostered days calculated using rota probability per cohort', ''],
  ];
  
  const assumptionsSheet = XLSX.utils.aoa_to_sheet(assumptionsData);
  XLSX.utils.book_append_sheet(workbook, assumptionsSheet, 'Assumptions');
  
  // Generate file
  const filename = `SSP_Model_${scenarioName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, filename);
  
  return filename;
}

/**
 * Export workforce model to PDF
 * @param {object} data - Model data to export
 * @param {string} scenarioName - Name of the scenario
 */
export function exportToPDF(data, scenarioName = 'SSP Model') {
  const { cohortResults, totals, sicknessPattern } = data;
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('UK SSP Cost Model', pageWidth / 2, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`Scenario: ${scenarioName}`, pageWidth / 2, 28, { align: 'center' });
  doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, pageWidth / 2, 34, { align: 'center' });
  
  // Summary section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Executive Summary', 14, 48);
  
  const summaryTableData = [
    ['Metric', 'Current Rules', 'April 2026', 'Change'],
    ['Total Employees', totals.employeeCount.toString(), totals.employeeCount.toString(), '-'],
    ['SSP Days Paid (Annual)', totals.currentSSPDays.toFixed(0), totals.newSSPDays.toFixed(0), `+${totals.additionalDaysPaid.toFixed(0)}`],
    ['SSP Cost', formatCurrency(totals.currentSSPCost), formatCurrency(totals.newSSPCost), formatCurrency(totals.sspCostIncrease)],
    ['Total Cost (inc. backfill)', formatCurrency(totals.currentTotalCost), formatCurrency(totals.newTotalCost), formatCurrency(totals.totalCostIncrease)],
    ['Cost Per Employee', formatCurrency(totals.currentCostPerEmployee), formatCurrency(totals.newCostPerEmployee), '-'],
  ];
  
  doc.autoTable({
    startY: 52,
    head: [summaryTableData[0]],
    body: summaryTableData.slice(1),
    theme: 'striped',
    headStyles: { fillColor: [79, 70, 229] },
    styles: { fontSize: 10 },
    columnStyles: {
      3: { fontStyle: 'bold', textColor: [220, 38, 38] }
    }
  });
  
  // Key impact callout
  const finalY = doc.lastAutoTable.finalY + 10;
  doc.setFillColor(254, 243, 199);
  doc.roundedRect(14, finalY, pageWidth - 28, 25, 3, 3, 'F');
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(146, 64, 14);
  doc.text('Key Impact:', 20, finalY + 10);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `The April 2026 changes will increase annual SSP costs by ${formatCurrency(totals.sspCostIncrease)} (${formatPercent(totals.percentSSPIncrease)})`,
    20, finalY + 18
  );
  doc.setTextColor(0, 0, 0);
  
  // Cohort breakdown
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Cohort Breakdown', 14, finalY + 38);
  
  const cohortTableData = cohortResults.map(c => [
    c.cohort,
    c.employeeCount.toString(),
    formatCurrency(c.weeklyEarnings),
    c.meetsLEL ? 'Yes' : 'No',
    formatCurrency(c.current.sspCost),
    formatCurrency(c.new2026.sspCost),
    formatCurrency(c.impact.additionalSSPCost),
  ]);
  
  doc.autoTable({
    startY: finalY + 42,
    head: [['Cohort', 'Count', 'Weekly £', 'LEL', 'Current SSP', '2026 SSP', 'Increase']],
    body: cohortTableData,
    theme: 'striped',
    headStyles: { fillColor: [79, 70, 229] },
    styles: { fontSize: 9 },
  });
  
  // Add new page for details
  doc.addPage();
  
  // Why costs increase section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Why Costs Increase Under 2026 Rules', 14, 20);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const reasons = [
    `1. Day 1 Payment: Currently ${(data.sicknessPattern.distribution.oneDay + data.sicknessPattern.distribution.twoDay + data.sicknessPattern.distribution.threeDay).toFixed(0)}% of absences are 1-3 days and cost nothing. From 2026, all will be payable.`,
    `2. No Waiting Period: The 3-day waiting period is removed, so days 1-3 of longer absences become payable.`,
    `3. Universal Eligibility: ${totals.employeesBelowLEL} employees currently don't qualify due to LEL. All will qualify from 2026.`,
    `4. SSP days paid increase from ${totals.currentSSPDays.toFixed(0)} to ${totals.newSSPDays.toFixed(0)} per year (+${((totals.newSSPDays/totals.currentSSPDays - 1) * 100).toFixed(0)}%).`,
  ];
  
  let yPos = 28;
  reasons.forEach(reason => {
    const lines = doc.splitTextToSize(reason, pageWidth - 28);
    doc.text(lines, 14, yPos);
    yPos += lines.length * 5 + 3;
  });
  
  // Sickness pattern
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Sickness Pattern Used', 14, yPos + 10);
  
  const patternTableData = [
    ['1 Day Absences', `${sicknessPattern.distribution.oneDay}%`, 'No SSP (currently)'],
    ['2 Day Absences', `${sicknessPattern.distribution.twoDay}%`, 'No SSP (currently)'],
    ['3 Day Absences', `${sicknessPattern.distribution.threeDay}%`, 'No SSP (currently)'],
    ['4-7 Day Absences', `${sicknessPattern.distribution.fourToSeven}%`, `Avg ${sicknessPattern.avgLengths.fourToSeven} days`],
    ['8+ Day Absences', `${sicknessPattern.distribution.eightPlus}%`, `Avg ${sicknessPattern.avgLengths.eightPlus} days`],
  ];
  
  doc.autoTable({
    startY: yPos + 14,
    head: [['Duration', 'Percentage', 'Notes']],
    body: patternTableData,
    theme: 'striped',
    headStyles: { fillColor: [107, 114, 128] },
    styles: { fontSize: 9 },
  });
  
  // Footer
  const footerY = doc.internal.pageSize.height - 15;
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text(
    'This model is for planning purposes only. Consult your payroll provider for accurate calculations.',
    pageWidth / 2, footerY, { align: 'center' }
  );
  doc.text(
    'Based on Employment Rights Act 2024 changes expected April 2026.',
    pageWidth / 2, footerY + 4, { align: 'center' }
  );
  
  // Save
  const filename = `SSP_Model_${scenarioName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
  
  return filename;
}

/**
 * Export individual calculation to PDF
 * @param {object} data - Individual calculation data
 */
export function exportIndividualToPDF(data) {
  const { employee, aweResult, currentSSP, newSSP, backfill } = data;
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('SSP Calculation', pageWidth / 2, 20, { align: 'center' });
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Employee: ${employee.id || 'Individual'}`, pageWidth / 2, 28, { align: 'center' });
  doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, pageWidth / 2, 34, { align: 'center' });
  
  // AWE Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('8-Week Average Weekly Earnings', 14, 48);
  
  const aweTableData = aweResult.earnings.map((e, i) => [
    `Week ${i + 1}`,
    formatCurrency(e),
  ]);
  aweTableData.push(['Total', formatCurrency(aweResult.total)]);
  aweTableData.push(['AWE', formatCurrency(aweResult.awe)]);
  aweTableData.push([`Meets LEL (${formatCurrency(LOWER_EARNINGS_LIMIT)})`, aweResult.meetsLEL ? 'Yes' : 'No']);
  
  doc.autoTable({
    startY: 52,
    body: aweTableData,
    theme: 'plain',
    styles: { fontSize: 10 },
    columnStyles: { 1: { halign: 'right' } },
  });
  
  // SSP Comparison
  const compY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('SSP Calculation Comparison', 14, compY);
  
  const sspTableData = [
    ['Metric', 'Current Rules', 'April 2026'],
    ['Eligible', currentSSP.eligible ? 'Yes' : 'No', 'Yes'],
    ['Waiting Days', currentSSP.waitingDays?.toString() || '3', '0'],
    ['Daily Rate', formatCurrency(currentSSP.dailyRate || 0), formatCurrency(newSSP.dailyRate)],
    ['Days Paid', currentSSP.sspDaysPaid?.toString() || '0', newSSP.sspDaysPaid.toString()],
    ['Total SSP', formatCurrency(currentSSP.totalSSP || 0), formatCurrency(newSSP.totalSSP)],
  ];
  
  doc.autoTable({
    startY: compY + 4,
    head: [sspTableData[0]],
    body: sspTableData.slice(1),
    theme: 'striped',
    headStyles: { fillColor: [79, 70, 229] },
    styles: { fontSize: 10 },
  });
  
  const filename = `SSP_Calculation_${employee.id || 'individual'}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
  
  return filename;
}

/**
 * Parse Excel file for employee data import
 * @param {File} file - Excel file to parse
 * @returns {Promise<Array>} Parsed employee data
 */
export async function parseEmployeeExcel(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        
        // Map to expected format
        const employees = jsonData.map((row, index) => ({
          id: row.employeeId || row.employee_id || row.id || `EMP${index + 1}`,
          name: row.name || row.employeeName || `Employee ${index + 1}`,
          contractedHours: parseFloat(row.contractedHours || row.contracted_hours || 0),
          hourlyRate: parseFloat(row.hourlyRate || row.hourly_rate || 11.44),
          qualifyingDays: parseInt(row.qualifyingDays || row.qualifying_days || 5),
          weeklyEarnings: row.week1 ? [
            parseFloat(row.week1 || 0),
            parseFloat(row.week2 || 0),
            parseFloat(row.week3 || 0),
            parseFloat(row.week4 || 0),
            parseFloat(row.week5 || 0),
            parseFloat(row.week6 || 0),
            parseFloat(row.week7 || 0),
            parseFloat(row.week8 || 0),
          ] : null,
        }));
        
        resolve(employees);
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error.message}`));
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}
